"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { FileText, Film, ImageIcon, Loader2, Music, Paperclip, Play, X } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { MediaLightbox, type MediaLightboxItem } from "@/components/media-lightbox"
import { uploadAttachment } from "@/modules/bugfix-agent/lib/client/api"
import { attachmentKind, MAX_ATTACHMENTS, type Attachment, type AttachmentKind } from "@/modules/bugfix-agent/lib/shared/types"
import { formatSize } from "./status-style"

export interface UploadItem {
  key: string
  name: string
  size: number
  kind: AttachmentKind
  progress: number
  status: "uploading" | "done" | "error"
  error?: string
  attachment?: Attachment
  preview?: string
  abort?: AbortController
}

const PARALLEL = 3

export type Uploader = (file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal) => Promise<Attachment>

/** Очередь загрузок вложений: файлы уходят на сервер сразу после выбора, в запрос — только id */
export function useAttachmentUploads(max = MAX_ATTACHMENTS, upload: Uploader = uploadAttachment) {
  const uploadRef = useRef(upload)
  uploadRef.current = upload
  const [items, setItems] = useState<UploadItem[]>([])
  const queue = useRef<Array<{ key: string; file: File }>>([])
  const active = useRef(0)
  const itemsRef = useRef(items)
  itemsRef.current = items

  const patch = useCallback((key: string, p: Partial<UploadItem>) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...p } : i))), [])

  const pump = useCallback(() => {
    while (active.current < PARALLEL && queue.current.length) {
      const job = queue.current.shift()!
      const abort = new AbortController()
      active.current++
      patch(job.key, { abort })
      uploadRef.current(job.file, (f) => patch(job.key, { progress: f }), abort.signal)
        .then((attachment) => patch(job.key, { status: "done", progress: 1, attachment }))
        .catch((e: Error) => {
          if (!abort.signal.aborted) {
            patch(job.key, { status: "error", error: e.message })
            toast.error(e.message)
          }
        })
        .finally(() => {
          active.current--
          pump()
        })
    }
  }, [patch])

  const add = useCallback(
    (files: FileList | File[] | null | undefined) => {
      const list = Array.from(files ?? [])
      if (!list.length) return
      const free = max - itemsRef.current.length
      if (free <= 0) {
        toast.error(`Не больше ${max} вложений`)
        return
      }
      if (list.length > free) toast.warning(`Добавлено ${free} из ${list.length}: не больше ${max} вложений`)
      const accepted = list.slice(0, free)
      const next: UploadItem[] = accepted.map((file) => {
        const kind = attachmentKind(file.type, file.name)
        const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`
        queue.current.push({ key, file })
        return { key, name: file.name || "file", size: file.size, kind, progress: 0, status: "uploading", preview: kind === "image" || kind === "video" ? URL.createObjectURL(file) : undefined }
      })
      setItems((cur) => [...cur, ...next])
      setTimeout(pump, 0)
    },
    [max, pump],
  )

  const remove = useCallback((key: string) => {
    setItems((list) => {
      const it = list.find((i) => i.key === key)
      it?.abort?.abort()
      if (it?.preview) URL.revokeObjectURL(it.preview)
      queue.current = queue.current.filter((q) => q.key !== key)
      return list.filter((i) => i.key !== key)
    })
  }, [])

  const reset = useCallback(() => {
    for (const it of itemsRef.current) if (it.preview) URL.revokeObjectURL(it.preview)
    queue.current = []
    setItems([])
  }, [])

  /** Уже загруженные файлы (правка отзыва) — без повторной загрузки */
  const preset = useCallback((list: Attachment[]) => {
    setItems(list.map((a) => ({ key: a.id, name: a.name, size: a.size, kind: a.kind, progress: 1, status: "done" as const, attachment: a, preview: undefined })))
  }, [])

  useEffect(() => () => itemsRef.current.forEach((it) => it.preview && URL.revokeObjectURL(it.preview)), [])

  return {
    items,
    add,
    remove,
    reset,
    preset,
    max,
    uploading: items.some((i) => i.status === "uploading"),
    failed: items.some((i) => i.status === "error"),
    ids: items.filter((i) => i.status === "done" && i.attachment).map((i) => i.attachment!.id),
    left: max - items.length,
  }
}

export type AttachmentUploads = ReturnType<typeof useAttachmentUploads>

const KIND_ICON: Record<AttachmentKind, typeof FileText> = { image: ImageIcon, video: Film, audio: Music, file: FileText }

function previewSrc(it: UploadItem): string | null {
  return it.preview || it.attachment?.url || null
}

/** Превью загружаемых файлов с прогрессом, просмотром и удалением */
export function UploadPreviews({ uploads, className }: { uploads: AttachmentUploads; className?: string }) {
  const [open, setOpen] = useState<number | null>(null)
  const viewable = uploads.items.filter((it) => (it.kind === "image" || it.kind === "video") && previewSrc(it))
  const lightbox: MediaLightboxItem[] = viewable.map((it) => ({
    src: previewSrc(it)!,
    type: it.kind === "video" ? "video" : "image",
    alt: it.name,
  }))
  if (!uploads.items.length) return null
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {uploads.items.map((it) => {
        const Icon = KIND_ICON[it.kind]
        const src = previewSrc(it)
        const index = viewable.findIndex((v) => v.key === it.key)
        const canOpen = index >= 0 && it.status !== "uploading"
        const media = (it.kind === "image" || it.kind === "video") && !!src
        return (
          <div
            key={it.key}
            className={cn(
              "group relative flex h-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-slate-50",
              media ? "min-w-10" : "w-16",
              it.status === "error" ? "border-red-300 bg-red-50" : "border-slate-200",
            )}
            title={`${it.name} · ${formatSize(it.size)}${it.error ? ` · ${it.error}` : ""}`}
          >
            {it.kind === "image" && src ? (
              <img src={src} alt="" className="h-full w-auto max-w-none" />
            ) : it.kind === "video" && src ? (
              <>
                <video src={src} className="pointer-events-none h-full w-auto max-w-none" muted preload="metadata" />
                <Play className="pointer-events-none absolute h-5 w-5 fill-white text-white drop-shadow" />
              </>
            ) : (
              <div className="flex flex-col items-center gap-0.5 px-1 text-center">
                <Icon className="h-5 w-5 text-slate-500" />
                <span className="w-full truncate text-[9px] leading-tight text-slate-500">{it.name}</span>
              </div>
            )}
            {canOpen && (
              <button type="button" className="absolute inset-0 z-10 cursor-pointer" aria-label={`Открыть ${it.name}`} onClick={() => setOpen(index)} />
            )}
            {it.status === "done" && it.attachment?.url && !canOpen && (
              <a href={it.attachment.url} target="_blank" rel="noreferrer" className="absolute inset-0 z-10" aria-label={`Открыть ${it.name}`} />
            )}
            {it.status === "uploading" && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/70">
                <Loader2 className="h-4 w-4 animate-spin text-slate-600" />
                <span className="mt-0.5 text-[10px] font-medium text-slate-600">{Math.round(it.progress * 100)}%</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => uploads.remove(it.key)}
              className="absolute right-0.5 top-0.5 z-30 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/75 text-white shadow ring-1 ring-white/60 transition-colors hover:bg-red-600"
              aria-label={`Удалить ${it.name}`}
              title="Удалить вложение"
              data-testid="attachment-remove"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )
      })}
      {open != null && <MediaLightbox items={lightbox} initialIndex={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

/** Вид кнопок скриншота и записи экрана (CaptureButtons) в строке действий AttachmentPicker */
export const CAPTURE_BUTTON = "h-7 gap-1.5 px-2 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 [&_svg:not([class*='size-'])]:size-3.5"

/** Кнопка «Прикрепить» + превью загружаемых файлов; children — дополнительные кнопки в строке действий */
export function AttachmentPicker({ uploads, className, compact, children, hint }: { uploads: AttachmentUploads; className?: string; compact?: boolean; children?: React.ReactNode; hint?: string }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <UploadPreviews uploads={uploads} />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <input
          ref={input}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            uploads.add(e.target.files)
            e.target.value = ""
          }}
        />
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={uploads.left <= 0}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
        >
          <Paperclip className="h-3.5 w-3.5" />
          {compact ? "" : "Прикрепить"}
        </button>
        {children}
        <span className="text-[11px] text-slate-400">
          {uploads.items.length ? `${uploads.items.length} из ${uploads.max}` : (hint ?? `фото, видео, любые файлы — до ${uploads.max}`)}
        </span>
      </div>
    </div>
  )
}

/** Обработчики перетаскивания и вставки файлов для контейнера */
export function useFileDrop(add: (files: FileList | File[]) => void) {
  const [over, setOver] = useState(false)
  return {
    over,
    props: {
      onDragOver: (e: React.DragEvent) => {
        if (!Array.from(e.dataTransfer.types).includes("Files")) return
        e.preventDefault()
        setOver(true)
      },
      onDragLeave: (e: React.DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return
        setOver(false)
      },
      onDrop: (e: React.DragEvent) => {
        if (!e.dataTransfer.files.length) return
        e.preventDefault()
        setOver(false)
        add(e.dataTransfer.files)
      },
      onPaste: (e: React.ClipboardEvent) => {
        const files = Array.from(e.clipboardData.files)
        if (files.length) {
          e.preventDefault()
          add(files)
        }
      },
    },
  }
}

/** Сетка сохранённых вложений с просмотром фото и видео */
export function AttachmentGallery({ items, size = "md" }: { items: Attachment[]; size?: "sm" | "md" }) {
  const [open, setOpen] = useState<number | null>(null)
  const media = items.filter((a) => a.kind === "image" || a.kind === "video")
  const files = items.filter((a) => a.kind !== "image" && a.kind !== "video")
  const lightbox: MediaLightboxItem[] = media.map((a) => ({ src: a.url, type: a.kind === "video" ? "video" : "image", alt: a.name }))
  const box = size === "sm" ? "h-14 min-w-10" : "h-20 min-w-12"
  if (!items.length) return null
  return (
    <div className="flex flex-col gap-2">
      {media.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {media.map((a, i) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setOpen(i)}
              className={cn("relative shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-shadow hover:shadow-md", box)}
              title={a.name}
            >
              {a.kind === "image" ? (
                <img src={a.url} alt={a.name} className="block h-full w-auto max-w-none" loading="lazy" />
              ) : (
                <>
                  <video src={`${a.url}#t=0.1`} className="block h-full w-auto max-w-none" muted preload="metadata" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play className="h-5 w-5 fill-white text-white" />
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((a) => {
            const Icon = KIND_ICON[a.kind]
            return (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-[240px] items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                <span className="truncate">{a.name}</span>
                <span className="shrink-0 text-slate-400">{formatSize(a.size)}</span>
              </a>
            )
          })}
        </div>
      )}
      {open != null && <MediaLightbox items={lightbox} initialIndex={open} onClose={() => setOpen(null)} />}
    </div>
  )
}
